import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Award,
  Check,
  ChevronRight,
  Copy,
  Gift,
  Loader2,
  MessageCircle,
  Sparkles,
  Star,
  Ticket,
  Users,
  Wallet,
} from "lucide-react";
import {
  enrollRewards,
  fetchRewards,
  redeemReward,
  RewardsConfig,
  RewardsPayload,
  RedeemResult,
} from "@/lib/rewards";

// ─────────────────────────────────────────────────────────────────────────────
// RewardsView — the Passes loyalty panel inside the widget: balance + tier,
// ways to earn/redeem (real redemption → real discount code), referral link,
// wallet buttons, recent activity. Identity = the widget's visitor-session
// token; everything member-scoped is resolved server-side (lib/rewards.ts).
// Design language mirrors OrdersView: gray-50 canvas, floating chrome pills,
// white rounded-2xl cards, tiny uppercase section labels.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  organizationId: string;
  contactId: string | null;
  config?: RewardsConfig | null;
  onBack: () => void;
  /** Open the chat view — used to identify unidentified visitors + as escape hatch. */
  onOpenChat?: () => void;
  /** The storefront (Shopify orgs): "Apply at checkout" opens
   *  `${storefrontUrl}/discount/<code>?redirect=/checkout`, which pre-applies
   *  the code. Absent on Woo → the code is copied instead. */
  storefrontUrl?: string | null;
}

/** Shopify's discount link: applies the code to the session and lands on checkout. */
const applyAtCheckoutUrl = (storefrontUrl: string | null | undefined, code: string): string | null => {
  const base = (storefrontUrl || "").trim().replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(base)) return null;
  return `${base}/discount/${encodeURIComponent(code)}?redirect=%2Fcheckout`;
};

const goTo = (url: string) => {
  try {
    // The widget lives on the store page itself; when it is boxed in an iframe
    // (the preview page) the top window is the store, so navigate that.
    (window.top || window).location.assign(url);
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: "R", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$", NZD: "NZ$",
};
const sym = (cur: string) => CURRENCY_SYMBOLS[cur] || `${cur} `;

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{children}</div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm ${className || ""}`}>{children}</div>
);

const reasonLabel = (reason: string, description: string | null): string => {
  if (description) return description;
  const map: Record<string, string> = {
    order: "Points from an order",
    welcome: "Welcome bonus",
    redeem: "Reward redeemed",
    adjust: "Balance adjustment",
    expiry: "Points expired",
    referral: "Referral bonus",
  };
  return map[reason] || reason.replace(/_/g, " ");
};

const RewardsView: React.FC<Props> = ({ organizationId, contactId, config, onBack, onOpenChat, storefrontUrl }) => {
  const [data, setData] = useState<RewardsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [welcomePoints, setWelcomePoints] = useState<number | null>(null);
  const [redeeming, setRedeeming] = useState<number | null>(null);
  const [redeemResult, setRedeemResult] = useState<RedeemResult | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = async () => {
    if (!contactId) {
      setLoading(false);
      return;
    }
    const payload = await fetchRewards(organizationId, contactId);
    setData(payload);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, contactId]);

  // Unused, unexpired codes — the same set the checkout block offers an Apply
  // button for. The fresh redemption above is excluded so it never shows twice.
  const readyCodes = useMemo(() => {
    const now = Date.now();
    const dead = new Set(["used", "redeemed", "cancelled", "void", "expired"]);
    return (data?.redemptions || []).filter(
      (r) =>
        r.code &&
        r.code !== redeemResult?.code &&
        !dead.has(String(r.status || "").toLowerCase()) &&
        (!r.expires_at || new Date(r.expires_at).getTime() > now),
    );
  }, [data?.redemptions, redeemResult?.code]);

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      /* clipboard unavailable — the text is visible to copy by hand */
    }
  };

  const join = async () => {
    if (!contactId || joining) return;
    setJoining(true);
    const out = await enrollRewards(organizationId, contactId);
    if (out?.success) {
      if (out.welcome_points) setWelcomePoints(out.welcome_points);
      setData(out);
    }
    setJoining(false);
  };

  const redeem = async (optionIndex: number) => {
    if (!contactId || redeeming !== null) return;
    setRedeeming(optionIndex);
    setRedeemError(null);
    const out = await redeemReward(organizationId, contactId, optionIndex);
    if (out?.success && out.code) {
      setRedeemResult(out);
      await load(); // refresh balance + activity
    } else {
      setRedeemError(
        out?.error === "insufficient_points"
          ? `You need ${out?.needed ?? "more"} more points for that reward.`
          : "That didn't work — please try again, or ask us in the chat.",
      );
    }
    setRedeeming(null);
  };

  const program = data?.program;
  const member = data?.member;
  const currency = program?.currency || "ZAR";

  const tierProgress = useMemo(() => {
    const tiers = [...(program?.tiers || [])].sort((a, b) => a.min - b.min);
    if (!member || tiers.length === 0) return null;
    const lifetime = member.lifetime_points;
    const next = tiers.find((t) => t.min > lifetime);
    if (!next) return { next: null, pct: 100, remaining: 0 };
    const prev = [...tiers].reverse().find((t) => t.min <= lifetime);
    const floor = prev ? prev.min : 0;
    const span = Math.max(1, next.min - floor);
    return {
      next,
      pct: Math.min(100, Math.round(((lifetime - floor) / span) * 100)),
      remaining: next.min - lifetime,
    };
  }, [program?.tiers, member]);

  // ── Section content ────────────────────────────────────────────────────────

  const renderUnidentified = () => (
    <div className="h-full flex flex-col items-center justify-center gap-4 px-8 text-center">
      <div className="w-14 h-14 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
        <Gift size={24} className="text-gray-400" />
      </div>
      <div>
        <p className="text-gray-900 font-semibold text-base">
          {config?.join_title || "Join our rewards"}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          {config?.join_subtitle ||
            "Earn points on every order and turn them into discounts."}
        </p>
        <p className="text-gray-400 text-xs mt-2">
          Start a quick chat first so we know who you are — then your rewards live right here.
        </p>
      </div>
      {onOpenChat && (
        <button
          type="button"
          onClick={onOpenChat}
          className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <MessageCircle size={15} /> Start a chat
        </button>
      )}
    </div>
  );

  const renderJoin = () => (
    <div className="px-3 pb-4 space-y-3">
      <Card className="px-5 py-6 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-gray-900 flex items-center justify-center">
          <Sparkles size={20} className="text-white" />
        </div>
        <p className="text-gray-900 font-semibold text-base mt-3">
          {config?.join_title || "Join our rewards"}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          {config?.join_subtitle ||
            "Earn points on every order and turn them into discounts."}
        </p>
        {(program?.welcome_bonus || 0) > 0 && (
          <p className="text-gray-700 text-sm font-medium mt-2">
            Get {program!.welcome_bonus} points just for joining.
          </p>
        )}
        {config?.join_enabled !== false ? (
          <button
            type="button"
            onClick={join}
            disabled={joining}
            className="mt-4 inline-flex items-center gap-2 px-6 h-10 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            {joining ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
            {joining ? "Setting up your card…" : "Join now"}
          </button>
        ) : (
          onOpenChat && (
            <button
              type="button"
              onClick={onOpenChat}
              className="mt-4 inline-flex items-center gap-2 px-6 h-10 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              <MessageCircle size={15} /> Ask us in the chat
            </button>
          )
        )}
      </Card>
      {renderWaysToEarn()}
    </div>
  );

  const renderWaysToEarn = () =>
    program ? (
      <>
        <SectionLabel>Ways to earn</SectionLabel>
        <Card className="divide-y divide-gray-100">
          <div className="px-4 py-3 flex items-center gap-3">
            <Star size={16} className="text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              {program.points_per_currency} point{program.points_per_currency === 1 ? "" : "s"} for every{" "}
              {sym(currency)}1 you spend
            </span>
          </div>
          {program.welcome_bonus > 0 && (
            <div className="px-4 py-3 flex items-center gap-3">
              <Sparkles size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700">{program.welcome_bonus} points when you join</span>
            </div>
          )}
          {program.referrals?.referrer_points ? (
            <div className="px-4 py-3 flex items-center gap-3">
              <Users size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                {program.referrals.referrer_points} points for every friend you refer
              </span>
            </div>
          ) : null}
        </Card>
      </>
    ) : null;

  const renderMember = () => {
    if (!member || !program) return null;
    return (
      <div className="px-3 pb-4 space-y-3">
        {welcomePoints !== null && (
          <Card className="px-4 py-3 flex items-center gap-3 border-green-200 bg-green-50">
            <Check size={16} className="text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800">
              Welcome aboard — {welcomePoints} points are already on your card.
            </span>
          </Card>
        )}

        {/* Balance hero */}
        <Card className="px-5 py-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Your points
          </div>
          <div className="flex items-end justify-between mt-1">
            <span className="text-4xl font-bold text-gray-900 tabular-nums leading-none">
              {member.points_balance.toLocaleString()}
            </span>
            {member.tier && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-900 text-white text-[11px] font-medium">
                <Award size={11} /> {member.tier}
              </span>
            )}
          </div>
          {tierProgress?.next && (
            <div className="mt-4">
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gray-900 transition-[width] duration-500"
                  style={{ width: `${tierProgress.pct}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">
                {tierProgress.remaining.toLocaleString()} points to {tierProgress.next.name}
              </p>
            </div>
          )}
        </Card>

        {/* Fresh redemption result */}
        {redeemResult?.code && (
          <Card className="px-4 py-4 border-green-200">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Your discount code
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5">
              <span className="font-mono text-sm font-semibold text-gray-900 truncate">
                {redeemResult.code}
              </span>
              <button
                type="button"
                onClick={() => copyText(redeemResult.code!, "code")}
                className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-gray-900"
              >
                {copied === "code" ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
                {copied === "code" ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              {redeemResult.description}
              {redeemResult.expires_at ? ` · valid until ${fmtDate(redeemResult.expires_at)}` : ""} · use it at
              checkout
            </p>
            {applyAtCheckoutUrl(storefrontUrl, redeemResult.code) && (
              <button
                type="button"
                onClick={() => goTo(applyAtCheckoutUrl(storefrontUrl, redeemResult.code!)!)}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black"
              >
                <Ticket size={14} />
                Apply at checkout
              </button>
            )}
          </Card>
        )}
        {/* Codes redeemed earlier (here, on the pass page, via the agent) that are still valid */}
        {readyCodes.length > 0 && (
          <div className="space-y-2">
            <SectionLabel>Ready to use</SectionLabel>
            <Card>
              <div className="divide-y divide-gray-100">
                {readyCodes.map((r) => {
                  const url = applyAtCheckoutUrl(storefrontUrl, r.code);
                  return (
                    <div key={r.code} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-semibold text-gray-900 truncate">{r.code}</div>
                        <div className="text-[11px] text-gray-500">
                          {r.description || `${r.points_spent.toLocaleString()} points`}
                          {r.expires_at ? ` · until ${fmtDate(r.expires_at)}` : ""}
                        </div>
                      </div>
                      {url ? (
                        <button
                          type="button"
                          onClick={() => goTo(url)}
                          className="flex-shrink-0 rounded-lg bg-gray-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-black"
                        >
                          Apply
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => copyText(r.code, `ready-${r.code}`)}
                          className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-[12px] font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {copied === `ready-${r.code}` ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
                          {copied === `ready-${r.code}` ? "Copied" : "Copy"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}
        {redeemError && (
          <Card className="px-4 py-3 border-amber-200 bg-amber-50">
            <span className="text-sm text-amber-800">{redeemError}</span>
          </Card>
        )}

        {renderWaysToEarn()}

        {/* Ways to redeem */}
        {program.redemption_options.length > 0 && (
          <>
            <SectionLabel>Ways to redeem</SectionLabel>
            <Card className="divide-y divide-gray-100">
              {program.redemption_options.map((o) => {
                const affordable = member.points_balance >= o.points;
                return (
                  <div key={o.option_index} className="px-4 py-3 flex items-center gap-3">
                    <Ticket size={16} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 font-medium truncate">{o.description}</p>
                      <p className="text-[11px] text-gray-500">
                        {o.points.toLocaleString()} points
                        {!affordable ? ` · ${(o.points - member.points_balance).toLocaleString()} to go` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={!affordable || redeeming !== null}
                      onClick={() => redeem(o.option_index)}
                      className={`flex-shrink-0 h-8 px-3.5 rounded-full text-[12px] font-medium transition-colors ${
                        affordable
                          ? "bg-gray-900 text-white hover:bg-gray-800"
                          : "bg-gray-100 text-gray-400"
                      } disabled:opacity-60`}
                    >
                      {redeeming === o.option_index ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : affordable ? (
                        "Redeem"
                      ) : (
                        "Locked"
                      )}
                    </button>
                  </div>
                );
              })}
            </Card>
          </>
        )}

        {/* Referral */}
        {config?.show_referral !== false && data?.referral_url && (
          <>
            <SectionLabel>Refer a friend</SectionLabel>
            <Card className="px-4 py-4">
              <p className="text-sm text-gray-700">
                Share your link — your friend gets their own card
                {program.referrals?.referee_bonus ? ` and ${program.referrals.referee_bonus} bonus points` : ""}
                {program.referrals?.referrer_points
                  ? `, and you get ${program.referrals.referrer_points} points when they order`
                  : ""}
                .
              </p>
              <div className="mt-2.5 flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                <span className="text-[12px] text-gray-600 truncate">{data.referral_url}</span>
                <button
                  type="button"
                  onClick={() => copyText(data.referral_url!, "ref")}
                  className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-gray-900"
                >
                  {copied === "ref" ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
                  {copied === "ref" ? "Copied" : "Copy"}
                </button>
              </div>
            </Card>
          </>
        )}

        {/* Wallet */}
        {config?.show_wallet !== false && data?.install_url && (
          <Card className="divide-y divide-gray-100">
            <a
              href={data.install_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 flex items-center justify-between group"
            >
              <span className="flex items-center gap-3 text-sm text-gray-900 font-medium">
                <Wallet size={16} className="text-gray-400" /> Add your card to your wallet
              </span>
              <ChevronRight size={15} className="text-gray-400 group-hover:text-gray-600" />
            </a>
            {data.dashboard_url && (
              <a
                href={data.dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 flex items-center justify-between group"
              >
                <span className="flex items-center gap-3 text-sm text-gray-900 font-medium">
                  <Gift size={16} className="text-gray-400" /> View your full card
                </span>
                <ChevronRight size={15} className="text-gray-400 group-hover:text-gray-600" />
              </a>
            )}
          </Card>
        )}

        {/* Activity */}
        {config?.show_activity !== false && (data?.ledger?.length || 0) > 0 && (
          <>
            <SectionLabel>Recent activity</SectionLabel>
            <Card className="divide-y divide-gray-100">
              {data!.ledger!.slice(0, 8).map((row, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] text-gray-700 truncate">
                      {reasonLabel(row.reason, row.description)}
                    </p>
                    <p className="text-[10px] text-gray-400">{fmtDate(row.created_at)}</p>
                  </div>
                  <span
                    className={`flex-shrink-0 text-[13px] font-semibold tabular-nums ${
                      row.delta >= 0 ? "text-green-600" : "text-gray-500"
                    }`}
                  >
                    {row.delta >= 0 ? "+" : ""}
                    {row.delta.toLocaleString()}
                  </span>
                </div>
              ))}
            </Card>
          </>
        )}

        {onOpenChat && (
          <p className="px-1 text-[10px] text-gray-400">
            Questions about your points?{" "}
            <button
              type="button"
              onClick={onOpenChat}
              className="underline underline-offset-2 hover:text-gray-600"
            >
              Just ask us in the chat.
            </button>
          </p>
        )}
      </div>
    );
  };

  return (
    <motion.div
      className="relative h-full flex flex-col bg-gray-50"
      style={{ fontSize: "16px" }}
    >
      {/* Floating chrome */}
      <motion.div
        className="absolute top-3 left-3 right-3 z-20 flex items-center pointer-events-none"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <button
          type="button"
          onClick={onBack}
          className="pointer-events-auto h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={18} className="text-gray-700" />
        </button>
        <div className="pointer-events-auto ml-2 h-9 px-3.5 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center">
          <span className="text-[13px] font-semibold text-gray-900">
            {config?.card_title || "My rewards"}
          </span>
        </div>
      </motion.div>

      <div className="flex-1 overflow-y-auto pt-16">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Loading your rewards…</span>
          </div>
        ) : !contactId ? (
          renderUnidentified()
        ) : !data || data.enabled === false ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center text-gray-400">
            <Gift size={22} />
            <span className="text-sm">
              Rewards aren't available right now — please try again in a moment.
            </span>
          </div>
        ) : data.enrolled ? (
          renderMember()
        ) : (
          renderJoin()
        )}
      </div>
    </motion.div>
  );
};

export default RewardsView;
